import React from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import InfoTip from '../InfoTip';
import { GlossaryId } from '../../content/glossary';
import Sparkline from './Sparkline';
import { Change, Tone } from './model';

interface Props {
  label: string;
  value: string;
  /** What the value is out of, under it: "of 44 in these groups". */
  note?: string;
  change?: Change;
  /** What the change is against. */
  comparedWith?: string;
  daily?: number[];
  /** The (i) beside the label; a number whose words need no explaining has none. */
  tip?: GlossaryId;
  /** Pressing the card opens what is behind the number. */
  onPress?: () => void;
  pressHint?: string;
}

const TONE: Record<Tone, string> = { good: 'text-emerald-700', nudge: 'text-amber-700', steady: 'text-gray-600', none: 'text-gray-500' };
const LINE: Record<Tone, string> = { good: '#059669', nudge: '#d97706', steady: '#2563eb', none: '#2563eb' };

// One number on the Dashboard: the value, how it moved against the month before, and its trend.
export default function KpiCard({ label, value, note, change, comparedWith, daily, tip, onPress, pressHint }: Props) {
  const body = (
    <>
      <span className="block text-2xl font-semibold tabular-nums tracking-tight text-gray-900">{value}</span>
      {note && <span className="block text-xs text-gray-500">{note}</span>}
      {change && (
        <span className="mt-0.5 block text-xs">
          <span className={`inline-flex items-center gap-0.5 font-medium tabular-nums ${TONE[change.tone]}`}>
            {change.direction === 'up' && <ArrowUp className="h-3 w-3" aria-hidden />}
            {change.direction === 'down' && <ArrowDown className="h-3 w-3" aria-hidden />}
            {change.direction === 'flat' && <Minus className="h-3 w-3" aria-hidden />}
            {change.direction === 'up' && <span className="sr-only">Up </span>}
            {change.direction === 'down' && <span className="sr-only">Down </span>}
            {change.text}
          </span>
          {comparedWith && change.direction !== 'none' && <span className="text-gray-500"> {comparedWith}</span>}
        </span>
      )}
      {daily && daily.length > 1 && (
        <span className="mt-2 block">
          <Sparkline values={daily} color={change ? LINE[change.tone] : undefined} />
        </span>
      )}
    </>
  );

  return (
    <div className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200/70 transition-[box-shadow,transform] duration-150 motion-reduce:transition-none contrast-more:ring-gray-400 ${onPress ? 'hover:-translate-y-px hover:shadow-md' : ''}`}>
      <div className="mb-1 flex items-center">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        {tip && <InfoTip id={tip} />}
      </div>
      {onPress ? (
        <button
          type="button"
          onClick={onPress}
          title={pressHint}
          className="block w-full rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {body}
          {pressHint && <span className="sr-only">. {pressHint}</span>}
        </button>
      ) : (
        <div>{body}</div>
      )}
    </div>
  );
}
